import React, { createContext, useContext, useState, ReactNode } from 'react';
import Alert from './Alert';
import Confirm from './Confirm';

type AlertOptions = {
    type?: "info" | "success" | "error" | "warning";
    title?: string;
    text?: string;
    confirmText?: string;
};

type ConfirmOptions = {
    type?: "info" | "success" | "error" | "warning";
    title?: string;
    text?: string;
    confirmText?: string;
    cancelText?: string;
};

type AlertContextType = {
    showAlert: (options: AlertOptions) => Promise<void>;
    showConfirm: (options: ConfirmOptions) => Promise<boolean>;
};

const AlertContext = createContext<AlertContextType | undefined>(undefined);

export function AlertProvider({ children }: { children: ReactNode }) {
    const [alertState, setAlertState] = useState<{
        isOpen: boolean;
        options: AlertOptions;
        resolve?: () => void;
    }>({
        isOpen: false,
        options: {},
    });

    const [confirmState, setConfirmState] = useState<{
        isOpen: boolean;
        options: ConfirmOptions;
        resolve?: (value: boolean) => void;
    }>({
        isOpen: false,
        options: {},
    });

    const showAlert = (options: AlertOptions): Promise<void> => {
        return new Promise((resolve) => {
            setAlertState({
                isOpen: true,
                options,
                resolve,
            });
        });
    };

    const showConfirm = (options: ConfirmOptions): Promise<boolean> => {
        return new Promise((resolve) => {
            setConfirmState({
                isOpen: true,
                options,
                resolve,
            });
        });
    };

    const handleAlertClose = () => {
        setAlertState((prev) => ({ ...prev, isOpen: false }));
        setTimeout(() => {
            if (alertState.resolve) {
                alertState.resolve();
            }
        }, 200);
    };

    const handleConfirmConfirm = () => {
        setConfirmState((prev) => ({ ...prev, isOpen: false }));
        setTimeout(() => {
            if (confirmState.resolve) {
                confirmState.resolve(true);
            }
        }, 200);
    };

    const handleConfirmCancel = () => {
        setConfirmState((prev) => ({ ...prev, isOpen: false }));
        setTimeout(() => {
            if (confirmState.resolve) {
                confirmState.resolve(false);
            }
        }, 200);
    };

    return (
        <AlertContext.Provider value={{ showAlert, showConfirm }}>
            {children}
            <Alert
                isOpen={alertState.isOpen}
                onClose={handleAlertClose}
                {...alertState.options}
            />
            <Confirm
                isOpen={confirmState.isOpen}
                onConfirm={handleConfirmConfirm}
                onCancel={handleConfirmCancel}
                {...confirmState.options}
            />
        </AlertContext.Provider>
    );
}

export function useAlert() {
    const context = useContext(AlertContext);
    if (!context) {
        throw new Error('useAlert must be used within AlertProvider');
    }
    return context.showAlert;
}

export function useConfirm() {
    const context = useContext(AlertContext);
    if (!context) {
        throw new Error('useConfirm must be used within AlertProvider');
    }
    return context.showConfirm;
}

